import imageCompression from 'browser-image-compression';
import Compressor from 'compressorjs';

/**
 * Smart image compression utility that preserves original format
 * Uses hybrid approach: browser-image-compression + compressorjs fallback
 */

interface CompressionOptions {
  maxSizeMB?: number;
  targetReduction?: number; // 0.5 = 50% reduction
  maxDimension?: number;
  quality?: number;
}

/**
 * Primary compression using browser-image-compression
 * Fast, efficient, preserves original format
 */
const primaryCompress = async (file: File, options: CompressionOptions = {}): Promise<File> => {
  const {
    maxSizeMB = file.size / (1024 * 1024) * (options.targetReduction || 0.3),
    maxDimension = 2048,
    quality = 0.70
  } = options;

  const compressionOptions = {
    maxSizeMB,
    maxWidthOrHeight: maxDimension,
    useWebWorker: true,
    initialQuality: quality,
    alwaysKeepResolution: false,
    // Don't specify fileType to preserve original format
  };

  try {
    const compressed = await imageCompression(file, compressionOptions);
    return compressed;
  } catch (error) {
    console.error('Primary compression failed:', error);
    return file;
  }
};

/**
 * Fallback compression using compressorjs
 * Quality-focused, canvas-based, preserves original format
 */
const fallbackCompress = async (file: File, options: CompressionOptions = {}): Promise<File> => {
  const {
    maxDimension = 1600,
    quality = 0.70
  } = options;

  return new Promise((resolve) => {
    new Compressor(file, {
      quality,
      maxWidth: maxDimension,
      maxHeight: maxDimension,
      convertSize: Infinity, // Don't convert format
      success: (compressedFile) => {
        resolve(compressedFile as File);
      },
      error: (error) => {
        console.error('Fallback compression failed:', error);
        resolve(file); // Return original on error
      }
    });
  });
};

/**
 * Smart compression orchestrator
 * Automatically determines best compression strategy
 */
export const smartCompress = async (file: File, options: CompressionOptions = {}): Promise<File> => {
  const fileSizeMB = file.size / (1024 * 1024);
  const targetSizeMB = options.maxSizeMB || 1.5;

  // Skip compression if file is already small enough
  if (fileSizeMB <= targetSizeMB) {
    return file;
  }

  try {
    // Step 1: Primary compression
    let compressed = await primaryCompress(file, options);

    // Step 2: Check if we need fallback compression
    const compressedSizeMB = compressed.size / (1024 * 1024);
    if (compressedSizeMB > targetSizeMB) {
      compressed = await fallbackCompress(compressed, {
        ...options,
        quality: 0.65, // More aggressive for fallback
        maxDimension: 1400
      });
    }

    return compressed;
  } catch (error) {
    console.error('Compression failed completely:', error);
    return file; // Return original file as last resort
  }
};

/**
 * Batch compression for multiple files
 */
export const compressFiles = async (files: File[], options: CompressionOptions = {}): Promise<File[]> => {
  const compressedFiles: File[] = [];
  
  for (const file of files) {
    const compressed = await smartCompress(file, options);
    compressedFiles.push(compressed);
  }
  
  return compressedFiles;
};

/**
 * Specialized compression for profile images (profile picture and banner)
 * Ensures images are under 1MB for storage compatibility
 */
export const compressProfileImage = async (file: File, imageType: 'profile' | 'banner' = 'profile'): Promise<File> => {
  const MAX_SIZE_MB = 1; // Hard limit for storage
  const fileSizeMB = file.size / (1024 * 1024);

  // Skip compression if already under 1MB
  if (fileSizeMB <= MAX_SIZE_MB) {
    return file;
  }

  // Aggressive compression settings for profile images
  const compressionOptions: CompressionOptions = {
    maxSizeMB: MAX_SIZE_MB,
    maxDimension: imageType === 'profile' ? 800 : 1200, // Profile: 800px, Banner: 1200px
    quality: 0.85, // Start with good quality
  };

  try {
    let compressed = await primaryCompress(file, compressionOptions);
    let compressedSizeMB = compressed.size / (1024 * 1024);

    // If still over 1MB, try more aggressive compression
    if (compressedSizeMB > MAX_SIZE_MB) {
      compressed = await fallbackCompress(compressed, {
        maxDimension: imageType === 'profile' ? 600 : 1000,
        quality: 0.75,
      });
      compressedSizeMB = compressed.size / (1024 * 1024);
    }

    // Final attempt with very aggressive settings if still over 1MB
    if (compressedSizeMB > MAX_SIZE_MB) {
      compressed = await fallbackCompress(compressed, {
        maxDimension: imageType === 'profile' ? 500 : 800,
        quality: 0.65,
      });
      compressedSizeMB = compressed.size / (1024 * 1024);
    }

    const finalSizeMB = compressed.size / (1024 * 1024);
    
    if (finalSizeMB > MAX_SIZE_MB) {
    }

    return compressed;
  } catch (error) {
    console.error(`${imageType} image compression failed:`, error);
    return file; // Return original as fallback
  }
};

/**
 * Get compression preview info without actually compressing
 */
export const getCompressionPreview = (file: File, targetReduction: number = 0.3) => {
  const currentSizeMB = file.size / (1024 * 1024);
  const estimatedSizeMB = currentSizeMB * targetReduction;
  
  return {
    currentSize: currentSizeMB,
    estimatedSize: estimatedSizeMB,
    estimatedReduction: (1 - targetReduction) * 100,
    needsCompression: currentSizeMB > 1.5
  };
};
